import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const QueuePage: React.FC = () => {
  const [queueData, setQueueData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQueueStatus();
    const interval = setInterval(fetchQueueStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchQueueStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/queue`);
      setQueueData(response.data);
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-600">Loading queue status...</div>;
  }

  const { stats, current_job, upcoming_jobs } = queueData || {};

  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-bold text-gray-900">Generation Queue</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl text-center">
          <div className="text-3xl font-bold">{stats?.processing || 0}</div>
          <div className="text-sm opacity-90">Processing</div>
        </div>
        
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-xl text-center">
          <div className="text-3xl font-bold">{stats?.queued || 0}</div>
          <div className="text-sm opacity-90">Queued</div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl text-center">
          <div className="text-3xl font-bold">
            {stats?.estimated_wait_time ? `${Math.ceil(stats.estimated_wait_time / 60)}m` : '0m'}
          </div>
          <div className="text-sm opacity-90">Est. Wait</div>
        </div>
      </div>

      {current_job && (
        <div className="bg-white border-2 border-green-500 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-green-600 mb-4">Currently Processing</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="font-semibold text-lg">{current_job.job_name}</div>
              <div className="text-gray-600">{current_job.workflow_name} • by {current_job.username}</div>
              <div className="text-sm text-gray-500 mt-2">
                Started: {new Date(current_job.started_at).toLocaleTimeString()}
              </div>
            </div>
            <div className="flex items-center justify-end">
              <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                Processing...
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Queue ({upcoming_jobs?.length || 0} jobs)
        </h3>
        
        {upcoming_jobs?.length > 0 ? (
          <div className="space-y-4">
            {upcoming_jobs.map((job: any, index: number) => (
              <div key={job.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="flex items-center space-x-3">
                    <div className="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{job.job_name}</div>
                      <div className="text-sm text-gray-600">{job.workflow_name}</div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    by {job.username}
                  </div>
                  
                  <div className="text-right text-sm text-gray-500">
                    ~{Math.ceil(job.estimated_duration / 60)} min
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">No jobs in queue</div>
        )}
      </div>
    </div>
  );
};

export default QueuePage;
